import { Typography, Image } from 'antd'
import styled from 'styled-components'
import { TagDetail, BaseDrawer, SvgIcon } from '@/components'
import { useGetCaseDetail } from './utils'
import { useEffect } from 'react'
import { t } from '@/i18n'
import leakSeverity from '@/assets/imges/leak-severity.png'
import leakCenter from '@/assets/imges/leak-center.png'
import leakLow from '@/assets/imges/leak-low.png'
import leakHigh from '@/assets/imges/leak-high.png'
interface CaseDetailProps {
  visible: boolean
  onClose: () => void
  resourceId: string
  attackTypeList: any[]
}

const protocolMap = {
  ProtocolTypeDns: 'DNS',
  ProtocolTypeHttp: 'HTTP',
  ProtocolTypeHttps: 'HTTPS',
  ProtocolTypeTcp: 'TCP',
}

export const CaseDetail = ({ visible, onClose, resourceId, attackTypeList }: CaseDetailProps) => {
  const { Text } = Typography
  const [detail, getCaseDetail] = useGetCaseDetail()
  useEffect(() => {
    if (visible) {
      getCaseDetail({ resourceId }).catch(_ => {})
    }
  }, [visible])

  const renderLabel = (label: string) => (
    <Text className="label" type="secondary">
      {label}
    </Text>
  )

  const riskLevelMap = {
    RiskLevelLow: { label: t('低危'), class: 'leak-low', icon: <Image src={leakLow} width={19} /> },
    RiskLevelMiddle: {
      label: t('中危'),
      class: 'leak-center',
      icon: <Image src={leakCenter} width={19} />,
    },
    RiskLevelHigh: {
      label: t('高危'),
      class: 'leak-high',
      icon: <Image src={leakHigh} width={19} />,
    },
    RiskLevelGross: {
      label: t('严重'),
      class: 'leak-severity',
      icon: <Image src={leakSeverity} width={19} />,
    },
  }

  const resourceLevelMap = {
    ResourceLevelJunior: { label: t('初级'), class: 'resource-level-junior' },
    ResourceLevelMiddle: { label: t('中级'), class: 'resource-level-middle' },
    ResourceLevelSenior: { label: t('高级'), class: 'resource-level-senior' },
  }

  return (
    <BaseDrawer title={t('详情')} onClose={onClose} open={visible} props={{ width: 560 }}>
      <Container>
        <div className="content">
          {detail?.confidenceLevel && detail?.confidenceLevel === 'ConfidenceLevelLow' ? (
            <SvgIcon name="置信度-低" width={92} className="confidence" />
          ) : detail?.confidenceLevel === 'ConfidenceLevelMid' ? (
            <SvgIcon name="置信度-中" width={92} className="confidence" />
          ) : (
            <SvgIcon name="置信度-高" width={92} className="confidence" />
          )}

          <div className="item" style={{ display: 'flex' }}>
            <span style={{ width: '100px', display: 'inline-block' }}>
              {renderLabel(t('用例名称：'))}
            </span>
            <Typography.Text ellipsis={{ tooltip: true }}>{detail?.caseName}</Typography.Text>
          </div>
          <div className="item" style={{ display: 'flex' }}>
            <span style={{ width: '100px', display: 'inline-block' }}>
              {renderLabel(t('用例资源：'))}
            </span>
            <Typography.Text ellipsis={{ tooltip: true }}>{detail?.url || '-'}</Typography.Text>
          </div>

          <div className="item">
            {renderLabel(t('验证模式：'))}
            <Text className="content">{t('Agent → 外部情报')}</Text>
          </div>

          <div className="item">
            {renderLabel(t('危险等级：'))}
            <span>
              {riskLevelMap[detail?.riskLevel]?.icon}
              <span className={riskLevelMap[detail?.riskLevel]?.class}>
                {riskLevelMap[detail?.riskLevel]?.label || '-'}
              </span>
            </span>
          </div>

          <div className="item">
            {renderLabel(t('用例分级：'))}
            <span className={resourceLevelMap[detail?.resourceLevel]?.class}>
              {resourceLevelMap[detail?.resourceLevel]?.label || '-'}
            </span>
          </div>

          <div className="item">
            {renderLabel(t('攻击类型：'))}
            <Text className="content">
              {attackTypeList.filter(item => item.id === detail?.attackType)[0]?.name || '-'}
            </Text>
          </div>

          <div className="item">
            {renderLabel(t('攻击组织：'))}
            <Text className="content">{detail?.apt || '-'}</Text>
          </div>

          <div className="item">
            {renderLabel(t('协议类型：'))}
            <Text className="content">{protocolMap[detail?.protocol] || '-'}</Text>
          </div>

          {detail?.tacticList?.length > 0 && (
            <div className="item" style={{ display: 'flex', flexDirection: 'column' }}>
              {renderLabel('ATT&CK')}
              <div style={{ background: '#F8F9FB', padding: 10, borderRadius: 8, marginTop: 14 }}>
                {detail?.tacticList?.map((info, index) => (
                  <Text
                    key={index}
                    className="content"
                    style={{ display: 'block', marginBottom: 5 }}
                  >
                    {info.name}
                  </Text>
                ))}
              </div>
            </div>
          )}

          <div className="item">
            {detail?.tags && detail?.tags?.length > 0 && (
              <TagDetail resourceTagList={detail?.tags} />
            )}
          </div>

          <div className="item">
            {renderLabel(t('用例描述：'))}
            <div className="description">{detail?.describe}</div>
          </div>

          <div className="item">
            {renderLabel(t('解决方案：'))}
            <div className="description">{detail?.solution}</div>
          </div>
        </div>
      </Container>
    </BaseDrawer>
  )
}

const Container = styled.div`
  .content {
    .item {
      margin-bottom: 24px;

      .label {
        display: inline-block;
        width: 100px;
        color: #86909c;
        font-size: 14px;
      }

      .content {
        color: #333;
        font-size: 14px;
      }
    }

    .description {
      margin-top: 8px;
      padding: 16px 12px;
      background: #f8f9fb;
      border-radius: 8px;
      min-height: 80px;
      white-space: pre-wrap;
      word-break: break-all;
    }
  }
  .confidence {
    position: absolute;
    right: 14px;
    top: 105px;
  }

  .leak-low {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #e1ebfe;
    color: #6f9fff;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-center {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #ffedce;
    color: #ffa709;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-high {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #ffe1e1;
    color: #ff4141;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  .leak-severity {
    display: inline-block;
    width: 36px;
    height: 16px;
    background: #f5d9d9;
    color: #b70000;
    border-radius: 4px;
    margin-left: 10px;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }
  .resource-level-junior {
    display: inline-block;
    background: #6f9fff;
    color: #fff;
    border-radius: 4px;
    font-size: 12px;
    width: 36px;
    height: 16px;
    line-height: 16px;
    text-align: center;
  }
  .resource-level-middle {
    display: inline-block;
    background: #ffa709;
    color: #fff;
    border-radius: 4px;
    font-size: 12px;
    width: 36px;
    height: 16px;
    line-height: 16px;
    text-align: center;
  }
  .resource-level-senior {
    display: inline-block;
    background: #f44f4f;
    color: #fff;
    border-radius: 4px;
    font-size: 12px;
    width: 36px;
    height: 16px;
    line-height: 16px;
    text-align: center;
  }
`

export default CaseDetail
